import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { LBSubscriptionService, LBSubscriptionStatus } from "@/services/LBSubscriptionService";
import { FreeSubscriptionService } from "@/services/FreeSubscriptionService";
import type { FreeSubscription } from "@/common/model/FreeSubscription";
import type { SubscriptionReconcileItem } from "@/common/model/AdminUserDetails";
import { Tooltip } from "@/components/ui/Tooltip";
import { UnifiedSubscriptionsTable } from "./subscriptions/UnifiedSubscriptionsTable";
import { CustomerInsightsStrip } from "./subscriptions/CustomerInsightsStrip";
import {
    buildUnifiedRows,
    computeEffectiveEntitlement,
    computeCustomerInsights,
    type SubTier,
    type StripeSubscriptionRow,
} from "./subscriptions/productResolver";

interface ProDetailsTabProps {
    subscriptionStatus: LBSubscriptionStatus;
    /** Required for the "Revalidate with store" action; omit to hide the button. */
    userId?: number;
    /** Called after a successful revalidation so the parent refetches the receipt view. */
    onRefresh?: () => void | Promise<void>;
}

const formatDate = (timestamp: number | null): string => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

/**
 * Human-readable time-to-expiry relative to now, e.g. "in ~11 months", "in 6 days",
 * "today", or "expired 3 months ago". Coarse on purpose (years/months/days).
 */
const humanizeTimeLeft = (expiry: number | null, now: number): string => {
    if (!expiry) return "";
    const ms = expiry - now;
    const past = ms < 0;
    const days = Math.floor(Math.abs(ms) / 86_400_000);
    if (days === 0) return "today";
    let amount: string;
    if (days >= 365) {
        const years = Math.floor(days / 365);
        const months = Math.round((days % 365) / 30);
        amount = months > 0 ? `${years}y ${months}mo` : `${years} year${years > 1 ? "s" : ""}`;
    } else if (days >= 30) {
        amount = `${Math.round(days / 30)} month${Math.round(days / 30) > 1 ? "s" : ""}`;
    } else {
        amount = `${days} day${days > 1 ? "s" : ""}`;
    }
    return past ? `expired ${amount} ago` : `in ~${amount}`;
};

const TIER_DISPLAY: Record<SubTier, string> = {
    pro: "Pro",
    plus: "Plus",
    trial: "Trial",
    free: "Free",
    unknown: "Unverified",
};

const providerLabel = (provider: string | null): string => {
    if (provider === "apple") return "Apple";
    if (provider === "google") return "Google";
    return provider || "Store";
};

const tierLabel = (tier: string | null): string => {
    if (!tier) return "free";
    return tier;
};

// Status → human copy + colour. OK is success; the rest are informational/warning, not hard errors.
const statusMeta = (item: SubscriptionReconcileItem): { text: string; tone: "ok" | "warn" | "error" } => {
    switch (item.status) {
        case "OK":
            return item.tierChanged
                ? { text: `updated ${tierLabel(item.beforeTier)} → ${tierLabel(item.afterTier)}`, tone: "ok" }
                : { text: `confirmed ${tierLabel(item.afterTier)} (no change)`, tone: "ok" };
        case "NO_RECEIPT":
            return { text: "no record at the store", tone: "warn" };
        case "UNRESOLVED":
            return { text: "could not map store identity to this user", tone: "warn" };
        case "STORE_ERROR":
            return { text: item.error ? `store error: ${item.error}` : "store error", tone: "error" };
        default:
            return { text: item.status || "unknown", tone: "warn" };
    }
};

export const ProDetailsTab: React.FC<ProDetailsTabProps> = ({ subscriptionStatus, userId, onRefresh }) => {
    const { subscriptions = [], receipts = [], usedFreemiumCount = 0 } = subscriptionStatus;

    const [isReconciling, setIsReconciling] = useState(false);
    const [reconcileResults, setReconcileResults] = useState<SubscriptionReconcileItem[] | null>(null);
    const [rawOpen, setRawOpen] = useState<Record<number, boolean>>({});

    // Free-grant comps are not part of /admin/users/{id}; fetch + filter to this user.
    // (v1 client-side filter; a backend ?userId= filter is the planned fast-follow.)
    const [freeGrants, setFreeGrants] = useState<FreeSubscription[]>([]);
    const [grantsError, setGrantsError] = useState(false);
    const [stripeSubs, setStripeSubs] = useState<StripeSubscriptionRow[]>([]);

    useEffect(() => {
        if (userId == null) return;
        let cancelled = false;
        // Reset per-user state up front so a previous user's error banner or grants
        // can't leak onto the next user while the fetch is in flight. (Copilot review.)
        setGrantsError(false);
        setFreeGrants([]);
        setStripeSubs([]);
        // Stripe rows come from their own per-user endpoint: they are filtered out of
        // /free-subscription/all server-side, so a Stripe-paying rider would otherwise show
        // no subscription here at all. A failure is non-fatal — store rows and free grants
        // still render, and the banner tells support the picture may be incomplete.
        FreeSubscriptionService.fetchStripeForUser(userId)
            .then((subs) => {
                if (!cancelled) setStripeSubs(subs);
            })
            .catch(() => {
                if (!cancelled) setGrantsError(true);
            });
        FreeSubscriptionService.fetchAll()
            .then((all) => {
                if (cancelled) return;
                const mine = all.filter((g) => g.userId === userId);
                // Guard against an unexpectedly large client-side payload (plan doc). Per-user
                // grants are 1–3 rows; if /free-subscription/all ever returns a huge set we still
                // only keep this user's, but log so we notice the global list outgrew the v1 approach.
                if (all.length > 5000) {
                    console.warn(
                        `free-subscription/all returned ${all.length} rows; consider the backend ?userId= filter (SRF-C).`
                    );
                }
                setFreeGrants(mine);
            })
            .catch(() => {
                if (!cancelled) setGrantsError(true);
            });
        return () => {
            cancelled = true;
        };
    }, [userId]);

    // Snapshot "now" per user, not per mount: an inline Date.now() would change every render and
    // defeat the memo, but an empty-deps memo goes stale if the tab stays mounted across users and
    // would misclassify active/expired for later users. Keying on userId fixes both. (Copilot review.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const now = useMemo(() => Date.now(), [userId]);
    const rows = useMemo(
        () => buildUnifiedRows(receipts, subscriptions, freeGrants, now, stripeSubs),
        [receipts, subscriptions, freeGrants, now, stripeSubs]
    );
    const entitlement = useMemo(() => computeEffectiveEntitlement(rows, now), [rows, now]);
    const insights = useMemo(() => computeCustomerInsights(rows, now), [rows, now]);

    const handleRevalidate = async () => {
        if (userId == null || isReconciling) return;
        setIsReconciling(true);
        try {
            const response = await LBSubscriptionService.reconcileSubscription(userId);
            const results = response.results ?? [];
            setReconcileResults(results);
            setRawOpen({});

            if (results.length === 0) {
                toast.info("No store receipts on file for this user");
            } else {
                const changed = results.filter((r) => r.status === "OK" && r.tierChanged);
                const errored = results.filter((r) => r.status === "STORE_ERROR");
                if (changed.length > 0) {
                    toast.success(
                        changed
                            .map((r) => `${providerLabel(r.provider)}: ${tierLabel(r.beforeTier)} → ${tierLabel(r.afterTier)}`)
                            .join(" · ")
                    );
                } else if (errored.length > 0) {
                    toast.error(`${providerLabel(errored[0].provider)}: ${errored[0].error || "store error"}`);
                } else {
                    toast.success("Subscription revalidated — no changes");
                }
            }

            if (onRefresh) await onRefresh();
        } catch {
            // requestHandler already surfaces the error; keep the card state untouched.
            toast.error("Revalidation failed — see console for details");
        } finally {
            setIsReconciling(false);
        }
    };

    const platformBadges = (
        <>
            {subscriptionStatus.isproios && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-100 text-blue-800">
                    iOS Pro
                </span>
            )}
            {subscriptionStatus.isproandroid && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-green-100 text-green-800">
                    Android Pro
                </span>
            )}
            {subscriptionStatus.freesubscription && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-emerald-100 text-emerald-800">
                    Free grant
                </span>
            )}
        </>
    );

    return (
        <div className="space-y-4">
            {/* Compact summary: effective entitlement + platform badges + revalidate + free sessions,
                all in one row to save vertical space. */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pb-3 border-b border-gray-200">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    {entitlement.tier === "free" ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Free
                        </span>
                    ) : (
                        <>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                {TIER_DISPLAY[entitlement.tier]}
                            </span>
                            <span className="text-gray-500">from</span>
                            <span className="text-gray-700">
                                {entitlement.source === "free_grant" ? "Free grant" : "Store"}
                            </span>
                            {entitlement.expiryDate && (
                                <span className="text-gray-500">
                                    · expires {formatDate(entitlement.expiryDate)}{" "}
                                    <span className="text-gray-400">({humanizeTimeLeft(entitlement.expiryDate, now)})</span>
                                </span>
                            )}
                            {!entitlement.verified && (
                                <Tooltip content="Effective tier is derived from a receipt we could not verify against the store. Revalidate to confirm.">
                                    <span className="ml-0.5 cursor-help text-amber-600 text-xs font-medium">⚠ unverified</span>
                                </Tooltip>
                            )}
                        </>
                    )}
                    {platformBadges}
                    {entitlement.overlapNote && (
                        <Tooltip content={entitlement.overlapNote}>
                            <span className="ml-0.5 cursor-help text-amber-600 text-xs">ⓘ overlap</span>
                        </Tooltip>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">
                        Free sessions <span className="font-medium text-gray-900">{usedFreemiumCount} / 5</span>
                    </span>
                    {userId != null && (
                        <button
                            type="button"
                            onClick={handleRevalidate}
                            disabled={isReconciling}
                            title="Re-check this user's subscription against Apple/Google and fix their tier"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                        >
                            <svg
                                className={`w-3.5 h-3.5 ${isReconciling ? "animate-spin" : ""}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                            </svg>
                            {isReconciling ? "Revalidating…" : "Revalidate with store"}
                        </button>
                    )}
                </div>
            </div>

            {/* Revalidation result card */}
            {reconcileResults && (
                <div className="rounded-lg border border-gray-200 bg-white">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                            Store revalidation result
                        </span>
                        <button
                            type="button"
                            onClick={() => setReconcileResults(null)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Dismiss"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {reconcileResults.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-gray-500 italic">
                            No store receipts on file for this user.
                        </p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {reconcileResults.map((item, index) => {
                                const meta = statusMeta(item);
                                const toneClass =
                                    meta.tone === "ok"
                                        ? "text-green-700"
                                        : meta.tone === "error"
                                        ? "text-red-700"
                                        : "text-amber-700";
                                const dotClass =
                                    meta.tone === "ok"
                                        ? "bg-green-500"
                                        : meta.tone === "error"
                                        ? "bg-red-500"
                                        : "bg-amber-500";
                                return (
                                    <li key={index} className="px-4 py-3">
                                        <div className="flex items-start gap-2">
                                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`} />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {providerLabel(item.provider)}
                                                    </span>
                                                    {item.status === "OK" && item.tierChanged && (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-800">
                                                            CHANGED
                                                        </span>
                                                    )}
                                                </div>
                                                <p className={`text-sm ${toneClass}`}>{meta.text}</p>
                                                {item.storeResponse != null && (
                                                    <div className="mt-1.5">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setRawOpen((prev) => ({ ...prev, [index]: !prev[index] }))
                                                            }
                                                            className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                                                        >
                                                            <svg
                                                                className={`w-3 h-3 transition-transform ${
                                                                    rawOpen[index] ? "rotate-90" : ""
                                                                }`}
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                                strokeWidth={2}
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    d="M9 5l7 7-7 7"
                                                                />
                                                            </svg>
                                                            raw store response
                                                        </button>
                                                        {rawOpen[index] && (
                                                            <pre className="mt-1.5 max-h-64 overflow-auto rounded bg-gray-900 p-3 text-[11px] leading-relaxed text-gray-100">
                                                                {JSON.stringify(item.storeResponse, null, 2)}
                                                            </pre>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}

            {/* Customer payment insights — compact stat strip (UI-derived from receipts). */}
            <CustomerInsightsStrip insights={insights} now={now} />

            {/* Unified subscription history (store purchases + free grants).
                Effective entitlement + free-sessions now live in the compact header above. */}
            <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Subscription history</h3>
                {grantsError && (
                    <div className="mb-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                        Free-grant data could not be loaded — store purchases below may be incomplete.
                    </div>
                )}
                {rows.length > 0 ? (
                    <UnifiedSubscriptionsTable rows={rows} />
                ) : (
                    <p className="text-sm text-gray-500 italic">
                        This user has never had Pro/Plus, a trial, or a free grant.
                    </p>
                )}
            </div>
        </div>
    );
};
